// Mobile port of the web Trash & Archive settings page
// (src/app/settings/page.tsx TrashSettingsContent). Same `trash` table via
// src/lib/trash.ts (byte-compatible with web) — browses deleted/archived
// items across ALL source components (Tasks, Notes, Projects, JNUS,
// Protocols...), with per-item Restore and Delete Forever, an Empty Trash/
// Archive action, and a kind (trash|archive) tab plus a source-component
// filter chip row.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles, DISPLAY_FONT } from '../../v10/theme';
import { SectionTitle, Chip, GlassCard } from '../../v10/ui';
import { supabase } from '../../lib/supabase';
import { emptyAllTrash, fetchTrash, moveToOtherKind, permanentDelete, restoreFromTrash, type TrashItem, type TrashKind } from '../../lib/trash';

export default function TrashEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeKind, setActiveKind] = useState<TrashKind>('trash');
  const [activeSource, setActiveSource] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchTrash(supabase, userId, activeKind);
      setItems(data);
    } catch (e) {
      Alert.alert('Failed to load', e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [userId, activeKind]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset the source filter whenever the underlying item set changes shape
  // (e.g. switching trash <-> archive) so a stale filter doesn't hide
  // everything.
  useEffect(() => {
    setActiveSource(null);
  }, [activeKind]);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) set.add(item.source_component);
    return Array.from(set).sort();
  }, [items]);

  const visibleItems = useMemo(() => {
    if (!activeSource) return items;
    return items.filter((item) => item.source_component === activeSource);
  }, [items, activeSource]);

  const handleRestore = (item: TrashItem) => {
    Alert.alert('Restore item?', `"${item.item_title}" will be restored to its original location.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Restore',
        onPress: async () => {
          setBusyId(item.id);
          try {
            await restoreFromTrash(supabase, item.id);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
          } catch (e) {
            Alert.alert('Restore failed', e instanceof Error ? e.message : 'Something went wrong.');
          } finally {
            setBusyId(null);
          }
        },
      },
    ]);
  };

  const handleDeleteForever = (item: TrashItem) => {
    Alert.alert('Delete permanently?', `"${item.item_title}" will be permanently removed. This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setBusyId(item.id);
          try {
            await permanentDelete(supabase, item.id);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
          } catch (e) {
            Alert.alert('Delete failed', e instanceof Error ? e.message : 'Something went wrong.');
          } finally {
            setBusyId(null);
          }
        },
      },
    ]);
  };

  const handleMoveToOtherKind = (item: TrashItem) => {
    const target: TrashKind = item.kind === 'archive' ? 'trash' : 'archive';
    Alert.alert(
      target === 'archive' ? 'Move to Archive?' : 'Move to Trash?',
      `"${item.item_title}" will be moved to ${target === 'archive' ? 'Archive' : 'Trash'}.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Move',
          onPress: async () => {
            setBusyId(item.id);
            try {
              await moveToOtherKind(supabase, item.id, target);
              setItems((prev) => prev.filter((i) => i.id !== item.id));
            } catch (e) {
              Alert.alert('Move failed', e instanceof Error ? e.message : 'Something went wrong.');
            } finally {
              setBusyId(null);
            }
          },
        },
      ]
    );
  };

  const handleEmptyAll = () => {
    if (!userId || visibleItems.length === 0) return;
    const label = activeKind === 'archive' ? 'Archive' : 'Trash';
    Alert.alert(
      `Empty All ${label}?`,
      `This will permanently delete all ${items.length} item(s) in ${label}. This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete All',
          style: 'destructive',
          onPress: async () => {
            try {
              await emptyAllTrash(supabase, userId, activeKind);
              setItems([]);
            } catch (e) {
              Alert.alert('Failed to empty', e instanceof Error ? e.message : 'Something went wrong.');
            }
          },
        },
      ]
    );
  };

  const activeLabel = activeKind === 'archive' ? 'Archive' : 'Trash';

  return (
    <View style={styles.container}>
      <SectionTitle style={styles.sectionGap}>Trash & Archive</SectionTitle>
      <Text style={styles.hint}>
        Deleted and archived items from across the app. Restore or permanently remove them.
      </Text>

      <View style={styles.kindRow}>
        <Chip label="Trash" active={activeKind === 'trash'} onPress={() => setActiveKind('trash')} />
        <Chip label="Archive" active={activeKind === 'archive'} onPress={() => setActiveKind('archive')} />
      </View>

      {sources.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.sourceScroll}>
          <View style={styles.sourceRow}>
            <Chip label={`All (${items.length})`} active={activeSource === null} onPress={() => setActiveSource(null)} />
            {sources.map((source) => {
              const count = items.filter((i) => i.source_component === source).length;
              return (
                <Chip key={source} label={`${source} (${count})`} active={activeSource === source} onPress={() => setActiveSource(source)} />
              );
            })}
          </View>
        </ScrollView>
      )}

      {visibleItems.length > 0 && (
        <Pressable style={styles.emptyAllBtn} onPress={handleEmptyAll}>
          <Text style={styles.emptyAllText}>Empty All {activeLabel}</Text>
        </Pressable>
      )}

      {loading ? (
        <Text style={styles.hint}>Loading…</Text>
      ) : visibleItems.length === 0 ? (
        <Text style={styles.hint}>{activeLabel} is empty.</Text>
      ) : (
        visibleItems.map((item) => (
          <GlassCard key={item.id} style={styles.card}>
            <View style={{ flex: 1 }}>
              <Text style={styles.itemSource}>{item.source_component}</Text>
              <Text style={styles.itemTitle} numberOfLines={1}>
                {item.item_title || 'Untitled'}
              </Text>
              <Text style={styles.itemMeta}>
                {item.item_type} · {formatDate(item.deleted_at)}
              </Text>
            </View>
            <View style={styles.actions}>
              <Pressable
                style={styles.actionChip}
                onPress={() => handleRestore(item)}
                disabled={busyId === item.id}
              >
                <Text style={styles.actionChipText}>Restore</Text>
              </Pressable>
              <Pressable
                style={styles.actionChip}
                onPress={() => handleMoveToOtherKind(item)}
                disabled={busyId === item.id}
              >
                <Text style={styles.actionChipText}>
                  {item.kind === 'archive' ? 'Move to Trash' : 'Move to Archive'}
                </Text>
              </Pressable>
              <Pressable
                style={styles.deleteChip}
                onPress={() => handleDeleteForever(item)}
                disabled={busyId === item.id}
              >
                <Text style={styles.deleteChipText}>Delete Forever</Text>
              </Pressable>
            </View>
          </GlassCard>
        ))
      )}
    </View>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  sectionGap: { marginTop: 18 },
  container: { gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },

  kindRow: { flexDirection: 'row', gap: 8, marginTop: 10 },

  sourceScroll: { marginTop: 10 },
  sourceRow: { flexDirection: 'row', gap: 8 },

  emptyAllBtn: {
    marginTop: 12,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  emptyAllText: { color: t.lapsed, fontWeight: '800', fontSize: 13 },

  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    marginTop: 8,
  },
  itemSource: { fontFamily: DISPLAY_FONT, fontSize: 11, fontWeight: '800', color: t.goldBright, letterSpacing: 0.5, textTransform: 'uppercase' },
  itemTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: t.ivory,
    marginTop: 2,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  itemMeta: { fontSize: 12, color: t.ivorySoft, marginTop: 2 },

  actions: { gap: 6 },
  actionChip: {
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingVertical: 6,
    paddingHorizontal: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  actionChipText: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  deleteChip: {
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingVertical: 6,
    paddingHorizontal: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteChipText: { fontSize: 12, fontWeight: '800', color: t.lapsed },
}),
);
